import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { gsap } from 'gsap';
import { useMenusGroupedByCategory } from '../../hooks/useMenuCategories';
import { useBeersGroupedByCategory } from '../../hooks/useBeerCategories';
import { useSettings } from '../../hooks/useSettings';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import decodeEntities from '../../utils/decodeEntities';
import { buildSrcSet } from '../../utils/imageVariants';
import './home.scss';

const API_URL = process.env.API_URL || 'http://localhost:7890';

function hexToRgba(hex, opacity) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// Hook for fetching public restaurant data by slug (polls with ETag, offline fallback)
function usePublicMenu(slug, { pollInterval = 10000 } = {}) {
  const cacheKey = slug ? `menu-cache-${slug}` : null;
  const [data, setData] = useState(() => {
    if (!cacheKey) return null;
    try { return JSON.parse(localStorage.getItem(cacheKey)); } catch { return null; }
  });
  const [isLoading, setIsLoading] = useState(!data);
  const [isOffline, setIsOffline] = useState(false);
  const etagRef = useRef(null);
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => {
    if (!slug) return;

    const fetchMenu = () => {
      const headers = {};
      if (etagRef.current) headers['If-None-Match'] = etagRef.current;

      fetch(`${API_URL}/api/v1/public/${slug}`, { headers })
        .then(res => {
          if (res.status === 304) { setIsOffline(false); return null; }
          if (!res.ok) throw new Error('Restaurant not found');
          etagRef.current = res.headers.get('ETag');
          return res.json();
        })
        .then(json => {
          if (json) {
            setData(json);
            setIsOffline(false);
            try { localStorage.setItem(cacheKey, JSON.stringify(json)); } catch { /* quota exceeded */ }
          }
        })
        .catch(() => {
          // Offline or server error — fall back to cached data
          if (!dataRef.current) {
            try {
              const cached = JSON.parse(localStorage.getItem(cacheKey));
              if (cached) { setData(cached); setIsOffline(true); }
              else setData(null);
            } catch { setData(null); }
          } else {
            setIsOffline(true);
          }
        })
        .finally(() => setIsLoading(false));
    };

    fetchMenu();
    const interval = setInterval(() => {
      if (!document.hidden) fetchMenu();
    }, pollInterval);

    // Immediately retry when connection comes back
    const handleOnline = () => fetchMenu();
    window.addEventListener('online', handleOnline);

    return () => { clearInterval(interval); window.removeEventListener('online', handleOnline); };
  }, [slug, pollInterval]);

  return { data, isLoading, isOffline };
}

export default function Home() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isTvMode = searchParams.get('tv') === '1';
  const isPrintMode = searchParams.get('print') === '1';

  // Public mode: fetch from public endpoint (no auth needed)
  // TV mode polls every 5s for faster updates when admin changes settings
  const { data: publicData, isLoading: publicLoading, isOffline } = usePublicMenu(slug, { pollInterval: isTvMode ? 5000 : 10000 });

  // Authenticated mode: use existing hooks (admin viewing their own menu)
  const { data: menusByCategory = [], isLoading: menusLoading } = useMenusGroupedByCategory({ enabled: !slug });
  const { data: beersByCategory = [], isLoading: beersLoading } = useBeersGroupedByCategory({ enabled: !slug });
  const { data: settings, isLoading: settingsLoading } = useSettings({ enabled: !slug });

  const loading = slug ? publicLoading : (menusLoading || beersLoading || settingsLoading);

  // Resolve data source based on mode
  const activeSettings = slug ? publicData?.settings : settings;
  const activeMenusByCategory = slug ? (publicData?.menusByCategory || []) : menusByCategory;
  const activeBeersByCategory = slug ? (publicData?.beersByCategory || []) : beersByCategory;
  // Extract settings with defaults
  const isDemo = publicData?.restaurant?.isDemo === true;
  const hasConverted = publicData?.restaurant?.tier && publicData.restaurant.tier !== 'free';
  const isDemoExpired = isDemo && !hasConverted && publicData?.restaurant?.demoExpiresAt && new Date(publicData.restaurant.demoExpiresAt) < new Date();
  const restaurantName = activeSettings?.restaurantName ?? publicData?.restaurant?.name ?? '';
  useDocumentTitle(restaurantName ? `${restaurantName} Menu` : 'Menu');
  const tagline = activeSettings?.tagline ?? '';
  const backgroundImage = activeSettings?.backgroundImageUrl;
  const logoUrl = activeSettings?.logoUrl || '';
  const logoSize = activeSettings?.logoSize || 'medium';
  const menuSectionTitle = activeSettings?.menuSectionTitle || 'Menu Items';
  const beerSectionTitle = activeSettings?.beerSectionTitle || 'Beers';
  const primaryFont = activeSettings?.primaryFont || 'Inter, system-ui, sans-serif';
  const customFontUrl = activeSettings?.customFontUrl || null;
  const customFontName = activeSettings?.customFontName || null;
  const heroTextColor = activeSettings?.heroTextColor || '#FFFFFF';
  const heroSubtitleColor = activeSettings?.heroSubtitleColor || '#FFFFFF';
  const heroBgColor = activeSettings?.heroBgColor || '#000000';
  const heroBgOpacity = activeSettings?.heroBgOpacity ?? 0.35;
  const unavailableDisplayMode = activeSettings?.unavailableDisplayMode ?? 'strikethrough';
  const unavailableLabel = activeSettings?.unavailableLabel ?? "86'd";
  const tvDisplayMode = activeSettings?.tvDisplayMode || 'static';
  const tvScrollSpeed = activeSettings?.tvScrollSpeed || 'medium';
  const backgroundImagePosition = (activeSettings?.backgroundImagePosition || 'center').replace('-', ' ');
  const enabledLanguages = activeSettings?.enabledLanguages || [];

  // Language picker state — TV mode can set language via ?lang=es query param
  const langParam = searchParams.get('lang');
  const [selectedLang, setSelectedLang] = useState(langParam || null);

  // Translation helper: returns saved translation or null
  const t = (item, field) => {
    if (!selectedLang || !item.translations?.[selectedLang]?.[field]) return null;
    return item.translations[selectedLang][field];
  };

  // Filter items based on unavailable display mode
  const filterItems = (items) => {
    if (unavailableDisplayMode === 'hidden') {
      return items.filter(item => item.available);
    }
    return items;
  };

  // Filter categories to only show those with visible items
  const visibleMenuCategories = activeMenusByCategory
    .map(cat => ({ ...cat, items: filterItems(cat.items || []) }))
    .filter(cat => cat.items.length > 0);

  const visibleBeerCategories = activeBeersByCategory
    .map(cat => ({ ...cat, items: filterItems(cat.items || []) }))
    .filter(cat => cat.items.length > 0);

  // Set body background and theme to match restaurant settings on public menu
  useEffect(() => {
    if (slug && activeSettings?.backgroundColor) {
      const bg = activeSettings.backgroundColor;
      const prevBg = document.body.style.backgroundColor;
      const prevTheme = document.documentElement.getAttribute('data-theme');
      document.body.style.backgroundColor = bg;

      // Determine if background is dark or light based on luminance
      const hex = bg.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      document.documentElement.setAttribute('data-theme', luminance < 0.5 ? 'dark' : 'light');

      return () => {
        document.body.style.backgroundColor = prevBg;
        document.documentElement.setAttribute('data-theme', prevTheme || 'light');
      };
    }
  }, [slug, activeSettings?.backgroundColor]);

  // Inject @font-face for custom font
  useEffect(() => {
    if (!customFontUrl || !customFontName) return;
    const styleId = 'custom-font-face';
    let style = document.getElementById(styleId);
    if (!style) { style = document.createElement('style'); style.id = styleId; document.head.appendChild(style); }
    style.textContent = `@font-face { font-family: '${customFontName}'; src: url('${customFontUrl}'); font-display: swap; }`;
    return () => { const el = document.getElementById(styleId); if (el) el.remove(); };
  }, [customFontUrl, customFontName]);

  const [textScale, setTextScale] = useState(100);
  const increaseText = () => setTextScale((s) => Math.min(s + 10, 150));
  const decreaseText = () => setTextScale((s) => Math.max(s - 10, 80));
  const resetText = () => setTextScale(100);

  const heroRef = useRef(null);
  const menuSectionRef = useRef(null);
  const beerSectionRef = useRef(null);

  useEffect(() => {
    if (loading) return;

    // Hero fade-in
    gsap.from(heroRef.current, {
      opacity: 0,
      y: 30,
      duration: 1,
      ease: 'power3.out'
    });

    // Stagger-in for menu items (simple delay-based, no ScrollTrigger)
    const allSections = document.querySelectorAll('.menu-category-section, .beer-category-section');
    allSections.forEach((section, sectionIndex) => {
      const title = section.querySelector('.category-title');
      const rows = section.querySelectorAll('.menu-row');
      const baseDelay = 0.3 + sectionIndex * 0.15;

      if (title) {
        gsap.from(title, { y: 15, opacity: 0, duration: 0.4, delay: baseDelay, ease: 'power2.out' });
      }
      gsap.from(rows, { y: 10, opacity: 0, stagger: 0.06, duration: 0.4, delay: baseDelay + 0.1, ease: 'power2.out' });
    });
  }, [loading]);

  // TV mode: request fullscreen on first user interaction
  useEffect(() => {
    if (!isTvMode || loading) return;

    const requestFullscreen = () => {
      const el = document.documentElement;
      if (!document.fullscreenElement && el.requestFullscreen) {
        el.requestFullscreen().catch(() => {});
      }
      document.removeEventListener('click', requestFullscreen);
      document.removeEventListener('keydown', requestFullscreen);
    };

    // Fullscreen requires user gesture — listen for first interaction
    document.addEventListener('click', requestFullscreen);
    document.addEventListener('keydown', requestFullscreen);

    return () => {
      document.removeEventListener('click', requestFullscreen);
      document.removeEventListener('keydown', requestFullscreen);
    };
  }, [isTvMode, loading]);

  // TV mode: display behavior based on settings
  const [activePage, setActivePage] = useState(0);
  useEffect(() => {
    if (!isTvMode || loading || tvDisplayMode === 'static' || tvDisplayMode === 'split') return;

    document.body.style.cursor = 'none';

    const speeds = { slow: 20, medium: 35, fast: 55 };
    const pageDurations = { slow: 12000, medium: 8000, fast: 5000 };

    if (tvDisplayMode === 'scroll') {
      const SCROLL_SPEED = speeds[tvScrollSpeed] || 35;
      const PAUSE_DURATION = 6000;
      let timeoutId;
      let direction = 1;
      let paused = false;

      const step = () => {
        if (!paused) {
          window.scrollBy(0, direction * 1);
          const maxScroll = document.documentElement.scrollHeight - window.innerHeight;

          if (window.scrollY >= maxScroll && direction === 1) {
            paused = true;
            timeoutId = setTimeout(() => { direction = -1; paused = false; }, PAUSE_DURATION);
            return;
          } else if (window.scrollY <= 0 && direction === -1) {
            paused = true;
            timeoutId = setTimeout(() => { direction = 1; paused = false; }, PAUSE_DURATION);
            return;
          }
        }
        timeoutId = setTimeout(() => requestAnimationFrame(step), 1000 / SCROLL_SPEED);
      };

      timeoutId = setTimeout(() => requestAnimationFrame(step), PAUSE_DURATION);
      return () => { document.body.style.cursor = ''; clearTimeout(timeoutId); };
    }

    if (tvDisplayMode === 'page') {
      const allCategories = [...visibleMenuCategories, ...visibleBeerCategories];
      if (allCategories.length === 0) return;

      const duration = pageDurations[tvScrollSpeed] || 8000;
      const interval = setInterval(() => {
        setActivePage(p => (p + 1) % allCategories.length);
      }, duration);

      return () => { document.body.style.cursor = ''; clearInterval(interval); };
    }
  }, [isTvMode, loading, tvDisplayMode, tvScrollSpeed, visibleMenuCategories.length, visibleBeerCategories.length]);

  // Auto-trigger print dialog when ?print=1
  useEffect(() => {
    if (!isPrintMode || loading) return;
    const timer = setTimeout(() => window.print(), 500);
    return () => clearTimeout(timer);
  }, [isPrintMode, loading]);

  const formatPrice = (price) => {
    const num = typeof price === 'number' ? price : parseFloat(price);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading the finest selections...</p>
      </div>
    );
  }

  if (slug && !publicData) {
    return (
      <div className="loading-container">
        {navigator.onLine ? (
          <>
            <h2>Restaurant not found</h2>
            <p>The menu you&apos;re looking for doesn&apos;t exist.</p>
          </>
        ) : (
          <>
            <h2>You&apos;re offline</h2>
            <p>Connect to the internet and reload to view this menu. Once loaded, it will be available offline.</p>
          </>
        )}
      </div>
    );
  }

  // Print mode: render a dead-simple layout that bypasses all complex CSS
  if (isPrintMode) {
    return (
      <div style={{ fontFamily: primaryFont, color: '#000', background: '#fff', padding: '0.5in' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          {logoUrl && <img src={logoUrl} alt={`${restaurantName} logo`} style={{ maxHeight: '60px', marginBottom: '0.5rem' }} />}
          <h1 style={{ fontSize: '1.8rem', margin: '0 0 0.25rem', fontWeight: 700 }}>{restaurantName}</h1>
          {tagline && <p style={{ fontSize: '0.85rem', margin: 0, color: '#666', letterSpacing: '2px', textTransform: 'uppercase' }}>{tagline}</p>}
          <hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '0.75rem auto', width: '80px' }} />
        </div>

        {visibleMenuCategories.map((cat) => (
          <div key={cat.category_id || cat.category_name} style={{ marginBottom: '1rem' }}>
            {cat.category_name && (
              <h3 style={{ fontSize: '1rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#333', margin: '0 0 0.4rem', paddingBottom: '0.2rem', borderBottom: '1px solid #ddd' }}>
                {cat.category_name}
              </h3>
            )}
            {cat.items.map((item) => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.25rem 0', borderBottom: '1px solid #eee', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', textDecoration: !item.available ? 'line-through' : 'none' }}>{item.item}</span>
                  {item.detail && <span style={{ display: 'block', fontSize: '0.75rem', color: '#666' }}>{item.detail}</span>}
                  {item.dietaryTags && item.dietaryTags.length > 0 && (
                    <span style={{ display: 'inline-flex', gap: '3px', marginTop: '2px' }} role="list" aria-label="Dietary information">
                      {item.dietaryTags.map(tag => (
                        <span key={tag} role="listitem" style={{ fontSize: '0.6rem', fontWeight: 600, padding: '1px 4px', borderRadius: '6px', background: '#f0e6d9', color: '#8B5E3C' }}>{tag}</span>
                      ))}
                    </span>
                  )}
                </div>
                <span style={{ fontWeight: 600, fontSize: '0.9rem', whiteSpace: 'nowrap', textDecoration: !item.available ? 'line-through' : 'none' }}>${formatPrice(item.price)}</span>
                {!item.available && unavailableLabel && (
                  <span style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#c00', border: '1px solid #c00', padding: '0.1rem 0.3rem', borderRadius: '2px', whiteSpace: 'nowrap' }}>{unavailableLabel}</span>
                )}
              </div>
            ))}
          </div>
        ))}

        {visibleBeerCategories.map((cat) => (
          <div key={cat.category_id || cat.category_name} style={{ marginBottom: '1rem' }}>
            {cat.category_name && (
              <h3 style={{ fontSize: '1rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#333', margin: '0 0 0.4rem', paddingBottom: '0.2rem', borderBottom: '1px solid #ddd' }}>
                {cat.category_name}
              </h3>
            )}
            {cat.items.map((item) => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.25rem 0', borderBottom: '1px solid #eee', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', textDecoration: !item.available ? 'line-through' : 'none' }}>{item.brewery}</span>
                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#666' }}>{item.style}{parseFloat(item.abv) > 0 ? ` · ${parseFloat(item.abv).toFixed(1)}% ABV` : ''}</span>
                </div>
                <span style={{ fontWeight: 600, fontSize: '0.9rem', whiteSpace: 'nowrap', textDecoration: !item.available ? 'line-through' : 'none' }}>${formatPrice(item.price)}</span>
                {!item.available && unavailableLabel && (
                  <span style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#c00', border: '1px solid #c00', padding: '0.1rem 0.3rem', borderRadius: '2px', whiteSpace: 'nowrap' }}>{unavailableLabel}</span>
                )}
              </div>
            ))}
          </div>
        ))}

        <div style={{ textAlign: 'center', fontSize: '0.7rem', color: '#999', borderTop: '1px solid #ddd', paddingTop: '0.5rem', marginTop: '1rem' }}>
          &copy; {new Date().getFullYear()} {restaurantName}. All rights reserved.
        </div>
      </div>
    );
  }

  // Apply restaurant settings as CSS custom properties
  // Base font size from settings scales everything via zoom
  const baseFontSize = activeSettings?.fontSize || 16;
  const fontZoom = (baseFontSize / 16) * (textScale / 100);
  const pColor = activeSettings?.primaryColor || '#D4AF37';
  const bgCol = activeSettings?.backgroundColor || '#1C1C1E';
  const txtCol = activeSettings?.textColor || '#F2F0ED';

  const homeStyle = {
    zoom: fontZoom,
    fontFamily: primaryFont,
    '--primary-font': primaryFont,
    '--primary-color': pColor,
    '--accent-color': activeSettings?.accentColor || undefined,
    '--background-color': bgCol,
    '--text-color': txtCol,
  };

  // Build unified category list for page-flip mode
  const allCategories = [
    ...visibleMenuCategories.map(c => ({ ...c, type: 'menu' })),
    ...visibleBeerCategories.map(c => ({ ...c, type: 'beer' })),
  ];
  const isPageMode = isTvMode && tvDisplayMode === 'page';
  const isSplitMode = isTvMode && tvDisplayMode === 'split';
  const currentPage = allCategories[activePage % allCategories.length];

  const isSpecialToday = (item) => {
    if (!item.isSpecial) return false;
    if (!item.specialDays || item.specialDays.length === 0) return true;
    return item.specialDays.includes(new Date().getDay());
  };

  const renderDietaryTags = (tags) => {
    if (!tags || tags.length === 0) return null;
    return (
      <span className="menu-row-dietary" role="list" aria-label="Dietary information">
        {tags.map(tag => <span key={tag} className="dietary-badge" role="listitem">{tag}</span>)}
      </span>
    );
  };

  const renderMenuItems = (items) => items.map((menu) => (
    <div key={menu.id} className={`menu-row ${menu.imageUrl ? 'menu-row-has-img' : ''} ${!menu.available ? `menu-row-${unavailableDisplayMode}` : ''}${isSpecialToday(menu) ? ' menu-row-special' : ''}`}>
      {menu.imageUrl && <img src={menu.imageUrl} srcSet={buildSrcSet(menu.imageUrl)} sizes="56px" alt={decodeEntities(t(menu, 'item') || menu.item)} className="menu-row-img" loading="lazy" />}
      <div className="menu-row-info">
        <span className="menu-row-name">
          {isSpecialToday(menu) && <span className="special-badge">{menu.specialLabel || 'Special'}</span>}
          {decodeEntities(t(menu, 'item') || menu.item)}
        </span>
        {(t(menu, 'detail') || menu.detail) && <span className="menu-row-desc">{decodeEntities(t(menu, 'detail') || menu.detail)}</span>}
        {renderDietaryTags(menu.dietaryTags)}
      </div>
      <span className="menu-row-price">${formatPrice(menu.price)}</span>
      {!menu.available && unavailableLabel && <span className="menu-row-badge">{unavailableLabel}</span>}
    </div>
  ));

  const renderBeerItems = (items) => items.map((beer) => (
    <div key={beer.id} className={`menu-row ${beer.imageUrl ? 'menu-row-has-img' : ''} ${!beer.available ? `menu-row-${unavailableDisplayMode}` : ''}${isSpecialToday(beer) ? ' menu-row-special' : ''}`}>
      {beer.imageUrl && <img src={beer.imageUrl} srcSet={buildSrcSet(beer.imageUrl)} sizes="56px" alt={decodeEntities(t(beer, 'brewery') || beer.brewery)} className="menu-row-img" loading="lazy" />}
      <div className="menu-row-info">
        <span className="menu-row-name">
          {isSpecialToday(beer) && <span className="special-badge">{beer.specialLabel || 'Special'}</span>}
          {decodeEntities(t(beer, 'brewery') || beer.brewery)}
        </span>
        <span className="menu-row-desc">{decodeEntities(t(beer, 'style') || beer.style)}{parseFloat(beer.abv) > 0 ? ` · ${parseFloat(beer.abv).toFixed(1)}% ABV` : ''}</span>
        {renderDietaryTags(beer.dietaryTags)}
      </div>
      <span className="menu-row-price">${formatPrice(beer.price)}</span>
      {!beer.available && unavailableLabel && <span className="menu-row-badge">{unavailableLabel}</span>}
    </div>
  ));

  const pageTitle = restaurantName ? `${restaurantName} Menu` : 'Menu';
  const metaDescription = tagline
    ? `${restaurantName} — ${tagline}. View the full menu online.`
    : `View the full ${restaurantName} menu online. Powered by Live Digital Menus.`;
  const menuUrl = slug ? `https://livedigitalmenus.com/menu/${slug}` : '';

  return (
    <div className={`home${isTvMode ? ' home--tv' : ''}${isPageMode ? ' home--page' : ''}${isSplitMode ? ' home--split' : ''}`} style={homeStyle} lang={selectedLang || 'en'}>
      {slug && (
        <Helmet>
          <title>{pageTitle} — Live Digital Menus</title>
          {isDemo && <meta name="robots" content="noindex, nofollow" />}
          <meta name="description" content={metaDescription} />
          <link rel="canonical" href={menuUrl} />
          <meta property="og:type" content="restaurant" />
          <meta property="og:url" content={menuUrl} />
          <meta property="og:title" content={`${pageTitle} — Live Digital Menus`} />
          <meta property="og:description" content={metaDescription} />
          {logoUrl && <meta property="og:image" content={logoUrl} />}
          <meta name="twitter:card" content={logoUrl ? 'summary_large_image' : 'summary'} />
          <meta name="twitter:title" content={`${pageTitle} — Live Digital Menus`} />
          <meta name="twitter:description" content={metaDescription} />
          {logoUrl && <meta name="twitter:image" content={logoUrl} />}
        </Helmet>
      )}
      {/* Expired demo — full-screen block */}
      {isDemoExpired && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, background: '#1C1C1E', color: '#F2F0ED', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', fontFamily: 'Inter, system-ui, sans-serif', textAlign: 'center', padding: '24px' }}>
          <p style={{ fontSize: '20px', fontWeight: 600, margin: '0 0 8px' }}>This preview has expired</p>
          <p style={{ fontSize: '16px', margin: 0 }}><a href="https://livedigitalmenus.com" style={{ color: '#D4AF37', textDecoration: 'underline' }}>livedigitalmenus.com</a></p>
        </div>
      )}
      {/* Demo banner — visible attribution for preview menus */}
      {isDemo && !isDemoExpired && !isTvMode && !isPrintMode && (
        <div id="demo-banner" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, background: '#D4AF37', color: '#1C1C1E', textAlign: 'center', padding: '8px 16px', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '14px', fontWeight: 600 }}>
          Preview — built by <a href="https://livedigitalmenus.com" style={{ color: '#1C1C1E', textDecoration: 'underline' }}>Live Digital Menus</a>
        </div>
      )}
      {/* Fullscreen hint for TV mode */}
      {isTvMode && !document.fullscreenElement && (
        <div className="tv-fullscreen-hint" aria-hidden="true">
          Click anywhere to enter fullscreen
        </div>
      )}

      {/* Offline indicator */}
      {isOffline && slug && (
        <div className="offline-banner" role="status">
          <span>Offline — showing cached menu</span>
        </div>
      )}

      {/* Text Size Controls */}
      {!isTvMode && (
        <div className="text-size-controls">
          <button onClick={decreaseText} disabled={textScale <= 80} aria-label="Decrease text size">A&minus;</button>
          <button onClick={resetText} className="text-size-reset" aria-label="Reset text size">{textScale}%</button>
          <button onClick={increaseText} disabled={textScale >= 150} aria-label="Increase text size">A+</button>
        </div>
      )}

      {/* Language Picker */}
      {slug && enabledLanguages.length > 0 && !isTvMode && (
        <div className="language-picker" role="group" aria-label="Menu language">
          <button
            className={`language-picker-btn ${!selectedLang ? 'active' : ''}`}
            onClick={() => setSelectedLang(null)}
            aria-pressed={!selectedLang}
          >
            EN
          </button>
          {enabledLanguages.map(code => (
            <button
              key={code}
              className={`language-picker-btn ${selectedLang === code ? 'active' : ''}`}
              onClick={() => setSelectedLang(code)}
              aria-pressed={selectedLang === code}
            >
              {code.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {/* Hero Section */}
      {!isPageMode && !isSplitMode && (
        <section
          ref={heroRef}
          className="hero"
          style={backgroundImage ? { backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.55)), url(${backgroundImage})`, backgroundPosition: backgroundImagePosition } : {}}
        >
          <div className="hero-content" style={{ background: hexToRgba(heroBgColor, heroBgOpacity) }}>
            {logoUrl && <img src={logoUrl} alt={`${restaurantName} logo`} className={`hero-logo hero-logo--${logoSize}`} />}
            {restaurantName && <h1 className="hero-title" style={{ color: heroTextColor }}>{restaurantName}</h1>}
            {tagline && <p className="hero-subtitle" style={{ color: heroSubtitleColor }}>{tagline}</p>}
            <div className="hero-divider" aria-hidden="true"></div>
          </div>
        </section>
      )}

      {/* Page-Flip Mode: one category at a time */}
      {isPageMode && currentPage && (
        <section className="tv-page" key={activePage}>
          <div
            className="tv-page-header"
            style={backgroundImage ? { backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.55)), url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: backgroundImagePosition } : {}}
          >
            <h1 className="hero-title" style={{ color: heroTextColor }}>{restaurantName}</h1>
            <div className="hero-divider" aria-hidden="true"></div>
          </div>
          <div className="tv-page-category">
            {currentPage.category_name && (
              <h2 className="tv-page-category-title">{currentPage.category_name}</h2>
            )}
            <div className="menu-list">
              {currentPage.type === 'menu'
                ? renderMenuItems(currentPage.items)
                : renderBeerItems(currentPage.items)
              }
            </div>
          </div>
          <div className="tv-page-dots">
            {allCategories.map((_, i) => (
              <span key={i} className={`tv-dot ${i === activePage % allCategories.length ? 'tv-dot--active' : ''}`} />
            ))}
          </div>
        </section>
      )}

      {/* Split-Screen Mode: food left, beverages right */}
      {isSplitMode && (
        <section className="tv-split">
          <div
            className="tv-split-header"
            style={backgroundImage ? { backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.55)), url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: backgroundImagePosition } : {}}
          >
            {logoUrl && <img src={logoUrl} alt={`${restaurantName} logo`} className={`hero-logo hero-logo--${logoSize}`} />}
            {restaurantName && <h1 className="hero-title" style={{ color: heroTextColor }}>{restaurantName}</h1>}
            {tagline && <p className="hero-subtitle" style={{ color: heroSubtitleColor }}>{tagline}</p>}
            <div className="hero-divider" aria-hidden="true"></div>
          </div>
          <div className="tv-split-columns">
            <div className="tv-split-pane tv-split-pane--food">
              <h2 className="tv-split-pane-title">{menuSectionTitle}</h2>
              {visibleMenuCategories.map((category) => (
                <div key={category.category_id || category.category_name} className="tv-split-category">
                  {category.category_name && (
                    <h3 className="tv-split-category-title">{category.category_name}</h3>
                  )}
                  <div className="menu-list">
                    {renderMenuItems(category.items)}
                  </div>
                </div>
              ))}
            </div>
            <div className="tv-split-divider"></div>
            <div className="tv-split-pane tv-split-pane--beverages">
              <h2 className="tv-split-pane-title">{beerSectionTitle}</h2>
              {visibleBeerCategories.map((category) => (
                <div key={category.category_id || category.category_name} className="tv-split-category">
                  {category.category_name && (
                    <h3 className="tv-split-category-title">{category.category_name}</h3>
                  )}
                  <div className="menu-list">
                    {renderBeerItems(category.items)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Standard Mode: full menu with categories grid */}
      {!isPageMode && !isSplitMode && (
        <>
          {/* Menu Section */}
          {visibleMenuCategories.length > 0 && (
          <section ref={menuSectionRef} className="menu-section">
            {!slug && (
              <div className="section-header">
                <h2 className="section-title">{menuSectionTitle}</h2>
                <div className="title-underline"></div>
              </div>
            )}

            <div className="categories-columns">
              {visibleMenuCategories.map((category) => {
                const shouldShowTitle = !!category.category_name;
                return (
                <div key={category.category_id || category.category_name} className="menu-category-section">
                  {shouldShowTitle && (
                    <h3 className="category-title">
                      {category.category_name}
                    </h3>
                  )}
                  <div className="menu-list">
                    {renderMenuItems(category.items)}
                  </div>
                </div>
                );
              })}
            </div>
          </section>
          )}

          {/* Beer Section */}
          {visibleBeerCategories.length > 0 && (
          <section ref={beerSectionRef} className="beer-section">
            {!slug && (
              <div className="section-header">
                <h2 className="section-title">{beerSectionTitle}</h2>
                <div className="title-underline"></div>
              </div>
            )}

            <div className="categories-columns">
              {visibleBeerCategories.map((category) => {
                const shouldShowTitle = !!category.category_name;
                return (
                <div key={category.category_id || category.category_name} className="beer-category-section">
                  {shouldShowTitle && (
                    <h3 className="category-title">
                      {category.category_name}
                    </h3>
                  )}
                  <div className="menu-list">
                    {renderBeerItems(category.items)}
                  </div>
                </div>
                );
              })}
            </div>
          </section>
          )}
        </>
      )}

      {/* Footer */}
      <footer className="home-footer">
        <p>&copy; {new Date().getFullYear()} {restaurantName}. All rights reserved.</p>
        <p className="powered-by">Powered by <span className="powered-by-brand">Live Digital Menus</span></p>
      </footer>
    </div>
  );
}
